// frontend/src/components/dashboard/pages/EmployeeTimesheetReport.tsx
// 📊 Employee Timesheet Report - A4 PORTRAIT - Full page fill
// ✅ html2canvas + jsPDF for pixel-perfect PDF
// ✅ No footer - content fills the page properly

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import styles from './EmployeeTimesheetReport.module.css';

// ============================================
// INTERFACES
// ============================================

interface AttendanceRecord {
    date: string;            
    dayOfWeek: string;
    checkIn: string | null;
    checkOut: string | null;
    status: 'Present' | 'Absent' | 'Late' | 'Incomplete' | 'Saturday OFF' | 'Holiday' | 'Sunday' | 'Compensation';
    regularHours: string;
    breakTime: string;
    defaultOTHours: string;
    extendedOTHours: string;
    sundayOTHours: string;
    holidayOTHours: string;
    noBreakOTHours: string;
    totalOTHours: string;
    totalWorkingHours: string;
    hasOTRequest: boolean;
    hasNoBreak: boolean;
    isWeekend?: boolean;
    isHoliday?: boolean;
    holidayName?: string;
    noBreakRequester?: string;
    otRequestDetails?: {
      startTime: string;
      endTime: string;
      requesterName: string;
    };
    remarks: string;
    isAbsentDay?: boolean;
    isHolidayDay?: boolean;
    isSundayDay?: boolean;
    isSaturdayOffDay?: boolean;
    isIncomplete?: boolean;
    isCompensationDay?: boolean;
    compensationRemarks?: string | null;
    hasCheckoutConcession?: boolean;
    checkoutConcessionApplied?: boolean;
    calculatedCheckOut?: string | null;
}

interface TimesheetOTSummary {
  totalRegularHours: string;
  totalBreakTime: string;
  totalDefaultOT: string;
  totalExtendedOT: string;
  totalSundayOT: string;
  totalHolidayOT: string;
  totalNoBreakOT: string;
  totalOTHours: string;
  totalWorkingHours: string;
  totalPresent: number;
  totalAbsent: number;
  totalLate: number;
  totalIncomplete: number;
  totalSaturdayOff: number;
  totalHolidays: number;
  totalSundays: number;
  totalNoBreakDays: number;
  totalCheckoutConcessions?: number;
  checkoutConcessionDates?: string[] | string;
  checkoutConcessionDatesString?: string;
}

interface EmployeeTimesheetData {
  auxId: string;
  name: string;
  projectCode: string;
  workTiming: string;
  regularTime: string;
  breakTime: string;
  defaultOTConfig: string;
  hasOvertimeEligibility: boolean;
  payrollPeriod: string;
  attendanceRecords: AttendanceRecord[];
  summary: TimesheetOTSummary;
}

// ============================================
// MAIN COMPONENT
// ============================================

const EmployeeTimesheetReport: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<EmployeeTimesheetData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const reportId = searchParams.get('reportId');
    if (!reportId) {
      setError('Invalid report link');
      return;
    }

    const storageKey = `timesheet_report_${reportId}`;
    const dataJson = sessionStorage.getItem(storageKey);
    
    if (!dataJson) {
      setError('Report data not found. Please generate the report again.');
      return;
    }

    try {
      const parsedData = JSON.parse(dataJson);
      setData(parsedData);
    } catch {
      setError('Failed to load report data');
    }
  }, [searchParams]);

  // ============================================
  // PDF GENERATION - A4 PORTRAIT (210 × 297mm)
  // ============================================

  const generatePDF = useCallback(async () => {
    if (!reportRef.current || !data) return;
    
    setIsGeneratingPDF(true);

    try {
      const reportEl = reportRef.current;

      const canvas = await html2canvas(reportEl, {
        scale: 2.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: reportEl.scrollWidth,
        windowHeight: reportEl.scrollHeight,
      });

      // A4 PORTRAIT
      const A4_WIDTH_MM = 210;
      const A4_HEIGHT_MM = 297;
      const MARGIN_X = 6;
      const MARGIN_Y = 5;

      const printW = A4_WIDTH_MM - (MARGIN_X * 2);  // 198mm
      const printH = A4_HEIGHT_MM - (MARGIN_Y * 2);  // 287mm

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const canvasAspect = canvas.width / canvas.height;
      const printAspect = printW / printH;

      let imgW: number, imgH: number;

      if (canvasAspect > printAspect) {
        imgW = printW;
        imgH = printW / canvasAspect;
      } else {
        imgH = printH;
        imgW = printH * canvasAspect;
      }

      const xOff = MARGIN_X + (printW - imgW) / 2;
      const yOff = MARGIN_Y;

      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', xOff, yOff, imgW, imgH);

      const empName = data.name.replace(/\s+/g, '_');
      const period = data.payrollPeriod.replace(/\s+/g, '_').replace(/[^\w-]/g, '');
      pdf.save(`Timesheet_${empName}_${period}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('PDF generation failed. Using browser print instead.');
      window.print();
    } finally {
      setIsGeneratingPDF(false);
    }
  }, [data]);

  // ============================================
  // HELPERS
  // ============================================

  const getStatusClass = (status: string, hasCheckoutConcession?: boolean) => {
    if (hasCheckoutConcession) return styles.statusConcession;
    const map: Record<string, string> = {
      'Present': styles.statusPresent,
      'Absent': styles.statusAbsent,
      'Late': styles.statusLate,
      'Incomplete': styles.statusIncomplete,
      'Saturday OFF': styles.statusSatOff,
      'Holiday': styles.statusHoliday,
      'Sunday': styles.statusSunday,
      'Compensation': styles.statusCompensation
    };
    return map[status] || '';
  };
 
  const isNonZero = (value: string): boolean => {
    if (!value || value === '-' || value === '0:00') return false;
    return true;
  };

  const getRowClass = (record: AttendanceRecord): string => {
    const classes: string[] = [];
    if (record.hasCheckoutConcession) classes.push(styles.concessionRow);
    if (record.isCompensationDay) {
      classes.push(styles.compensationRow);
    } else if (record.status === 'Sunday' || record.isSundayDay) {
      classes.push(styles.sundayRow);
    } else if (record.status === 'Holiday' || record.isHolidayDay) {
      classes.push(styles.holidayRow);
    } else if (record.status === 'Absent' || record.isAbsentDay) {
      classes.push(styles.absentRow);
    } else if (record.status === 'Saturday OFF' || record.isSaturdayOffDay) {
      classes.push(styles.saturdayOffRow);
    } else if (record.status === 'Incomplete' || record.isIncomplete) {
      classes.push(styles.incompleteRow);
    }
    return classes.join(' ');
  };

  const formatCheckoutConcessionDates = (dates: string[] | string | undefined): string => {
    if (!dates) return '-';
    try {
      if (Array.isArray(dates)) return dates.length > 0 ? dates.join(', ') : '-';
      if (typeof dates === 'string') return dates.includes(', ') ? dates : (dates || '-');
      return '-';
    } catch { return '-'; }
  };

  // ============================================
  // RENDER
  // ============================================

  if (error) {
    return (
      <div className={styles.errorPage}>
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => window.close()}>Close</button>
      </div>
    );
  }

  if (!data) {
    return <div className={styles.loading}>Loading...</div>;
  }

  const concessionDatesString = formatCheckoutConcessionDates(data.summary.checkoutConcessionDates);
  const hasConcession = data.summary.totalCheckoutConcessions && data.summary.totalCheckoutConcessions > 0;

  return (
    <div className={styles.page}>
      {/* Action buttons - hidden in print */}
      <div className={styles.actions}>
        <button 
          onClick={generatePDF} 
          className={`${styles.pdfBtn} ${isGeneratingPDF ? styles.btnDisabled : ''}`}
          disabled={isGeneratingPDF}
        >
          {isGeneratingPDF ? '⏳ Generating...' : '📥 Download PDF'}
        </button>
        <button onClick={() => window.print()} className={styles.printBtn}>
          🖨️ Print
        </button>
        <button onClick={() => window.close()} className={styles.closeBtn}>
          ✕ Close
        </button>
      </div>

      <div className={styles.report} ref={reportRef}>
        {/* ── HEADER ── */}
        <div className={styles.header}>
          <div className={styles.headerTitle}>PHOENICIAN TECHNICAL SERVICES LLC</div>
          <div className={styles.headerSub}>Employee Attendance & Overtime Timesheet</div>
        </div>

        {/* ── EMPLOYEE INFO ── */}
        <table className={styles.infoTable}>
          <tbody>
            <tr>
              <td><strong>Employee:</strong> {data.name}</td>
              <td><strong>AUX ID:</strong> {data.auxId}</td>
              <td><strong>Timing:</strong> {data.workTiming}</td>
              <td><strong>Period:</strong> {data.payrollPeriod}</td>
            </tr>
            <tr>
              <td><strong>Regular:</strong> {data.regularTime}</td>
              <td><strong>Break:</strong> {data.breakTime}</td>
              <td><strong>OT Eligible:</strong> {data.hasOvertimeEligibility ? 'Yes' : 'No'}</td>
              <td><strong>Default OT:</strong> {data.hasOvertimeEligibility ? data.defaultOTConfig : 'N/A'}</td>
            </tr>
          </tbody>
        </table>

        {/* ── SUMMARY ── */}
        <div className={styles.summaryBar}>
          <div className={styles.summaryLine}>
            <strong className={styles.slLabel}>Attendance:</strong>
            Present: <strong>{data.summary.totalPresent}</strong> | 
            Absent: <strong className={styles.absentText}>{data.summary.totalAbsent}</strong> | 
            Late: <strong>{data.summary.totalLate}</strong> | 
            Incomplete: <strong>{data.summary.totalIncomplete}</strong> | 
            Sat OFF: <strong>{data.summary.totalSaturdayOff}</strong> | 
            Holidays: <strong>{data.summary.totalHolidays}</strong> | 
            Sundays: <strong>{data.summary.totalSundays}</strong>
            {data.summary.totalNoBreakDays > 0 && (
              <> | No Break: <strong>{data.summary.totalNoBreakDays}</strong></>
            )}
            {hasConcession && (
              <> | <span className={styles.concessionInline}>CC: <strong>{data.summary.totalCheckoutConcessions}/3</strong></span></>
            )}
          </div>

          {hasConcession && concessionDatesString !== '-' && (
            <div className={styles.summaryLine}>
              <strong className={styles.slLabel}>Concession Dates:</strong>
              <span className={styles.concessionDatesText}>{concessionDatesString}</span>
              <span className={styles.concessionNote}> (auto-calculated per memo)</span>
            </div>
          )}

          {data.hasOvertimeEligibility && (
            <div className={styles.summaryLine}>
              <strong className={styles.slLabel}>Overtime:</strong>
              Default: <strong className={styles.defaultOTText}>{data.summary.totalDefaultOT}</strong> | 
              Extended: <strong className={styles.extendedOTText}>{data.summary.totalExtendedOT}</strong> | 
              Sunday: <strong className={styles.sundayOTText}>{data.summary.totalSundayOT}</strong> | 
              Holiday: <strong className={styles.holidayOTText}>{data.summary.totalHolidayOT || '0:00'}</strong> | 
              No Break: <strong className={styles.noBreakOTText}>{data.summary.totalNoBreakOT || '0:00'}</strong> | 
              <span className={styles.totalOTBadge}>Total OT: <strong>{data.summary.totalOTHours}</strong></span> | 
              <span className={styles.totalWorkBadge}>Total Working: <strong>{data.summary.totalWorkingHours}</strong></span>
            </div>
          )}

          {!data.hasOvertimeEligibility && (
            <div className={styles.summaryLine}>
              <span className={styles.totalWorkBadge}>
                Total Working: <strong>{data.summary.totalWorkingHours}</strong>
              </span>
            </div>
          )}
        </div>

        {/* ── ATTENDANCE TABLE ── */}
        <table className={styles.attendanceTable}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Day</th>
              <th>Check In</th>
              <th>Check Out</th>
              <th>Status</th>
              {data.hasOvertimeEligibility && (
                <>
                  <th>Def OT</th>
                  <th>Ext OT</th>
                  <th>Sun OT</th>
                  <th>Hol OT</th>
                  <th>NB OT</th>
                  <th className={styles.thTotalOT}>Total OT</th>
                </>
              )}
              <th>Work Hrs</th>
              <th>NB</th>
              <th className={styles.thRemarks}>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {data.attendanceRecords.map((record, idx) => (
              <tr key={idx} className={getRowClass(record)}>
                <td className={styles.tdDate}>{record.date}</td>
                <td className={styles.tdDay}>{record.dayOfWeek.substring(0, 3)}</td>
                <td className={styles.tdTime}>{record.checkIn || '-'}</td>
                <td className={`${styles.tdTime} ${record.hasCheckoutConcession ? styles.concessionCheckout : ''}`}>
                  {record.checkOut || '-'}
                  {record.hasCheckoutConcession && <span className={styles.concessionBadge} title="Checkout auto-calculated">📋</span>}
                </td>
                <td className={styles.tdStatus}>
                  <span className={getStatusClass(record.status, record.hasCheckoutConcession)}>
                    {record.hasCheckoutConcession ? 'CC' : record.status}
                  </span>
                </td>
                {data.hasOvertimeEligibility && (
                  <>
                    <td className={`${styles.tdOT} ${isNonZero(record.defaultOTHours) ? styles.otActive : ''}`}>
                      {record.defaultOTHours}
                    </td>
                    <td className={`${styles.tdOT} ${isNonZero(record.extendedOTHours) ? styles.extOTActive : ''}`}>
                      {record.extendedOTHours}
                      {record.hasOTRequest && isNonZero(record.extendedOTHours) && (
                        <span className={styles.otRequestBadge}>✓</span>
                      )}
                    </td>
                    <td className={`${styles.tdOT} ${isNonZero(record.sundayOTHours) ? styles.sunOTActive : ''}`}>
                      {record.sundayOTHours}
                    </td>
                    <td className={`${styles.tdOT} ${isNonZero(record.holidayOTHours) ? styles.holOTActive : ''}`}>
                      {record.holidayOTHours || '0:00'}
                    </td>
                    <td className={`${styles.tdOT} ${isNonZero(record.noBreakOTHours) ? styles.nbOTActive : ''}`}>
                      {record.noBreakOTHours || '0:00'}
                    </td>
                    <td className={`${styles.tdOT} ${styles.totalOTCell}`}>
                      <strong>{record.totalOTHours}</strong>
                    </td>
                  </>
                )}
                <td className={styles.tdHours}>{record.totalWorkingHours}</td>
                <td className={styles.tdNoBreak}>
                  {record.hasNoBreak ? <span className={styles.noBreakYes}>Y</span> : '-'}
                </td>
                <td className={styles.tdRemarks}>
                  {record.holidayName && record.status === 'Holiday' && !record.checkIn ? (
                    <span className={styles.holidayRemarkText}>{record.holidayName}</span>
                  ) : record.remarks !== '-' ? record.remarks : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmployeeTimesheetReport;